'use client';

import { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';

import logo from '../../../public/petrolboys-colored.svg';
import { twMerge } from 'tailwind-merge';
import { HamburgerButton } from './hamburger-button';
import { MobileMenu } from './mobile-menu';
import { Container } from '@/components/container/container';
import { Button } from '@/components/ui/button/button';
import { MenuLinks } from './menu-links';
import Link from 'next/link';
import { useCloseOnNavigation } from './use-close-navigation';

const scrollThreshold = 50;
const HEADER_ANIMATED_KEY = 'header-animated';

// Module-level flag - persists across client-side navigations
let hasAnimatedInRuntime = false;

export const Header = () => {
  const [sticky, setSticky] = useState(false);
  const [menuIsOpened, setMenuIsOpened] = useState(false);
  // null = not yet determined, true = animate, false = don't animate
  const [animationState, setAnimationState] = useState<'pending' | 'animate' | 'visible'>('pending');

  // Check if we should animate - only on first mount, client-side only
  useEffect(() => {
    if (hasAnimatedInRuntime) {
      setAnimationState('visible');
      return;
    }

    const hasAnimatedBefore = sessionStorage.getItem(HEADER_ANIMATED_KEY);
    if (!hasAnimatedBefore) {
      sessionStorage.setItem(HEADER_ANIMATED_KEY, 'true');
      setAnimationState('animate');
    } else {
      setAnimationState('visible');
    }
    hasAnimatedInRuntime = true;
  }, []);

  const displayBackground = sticky || menuIsOpened;

  const onScroll = useCallback(() => {
    setSticky(window.scrollY >= scrollThreshold);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  useCloseOnNavigation(() => {
    setMenuIsOpened(false);
  });

  useEffect(() => {
    setSticky(window.scrollY >= scrollThreshold);
  }, []);

  useEffect(() => {
    if (!menuIsOpened) {
      document.body.style.overflow = '';
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [menuIsOpened]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      if (event.matches) {
        setMenuIsOpened(false);
      }
    };

    handleChange(mediaQuery);
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  const toggleMenu = () => {
    setMenuIsOpened(prevState => !prevState);
  };

  return (
    <>
      <header
        className={twMerge(
          'fixed top-0 left-0 w-full z-50 py-4 lg:py-5 transition-all duration-500 ease-out text-foreground',
          'before:absolute before:pointer-events-none before:inset-x-6 before:-bottom-px before:h-px before:content-[""] before:bg-gradient-to-r before:from-transparent before:via-action/80 before:to-transparent before:opacity-0 before:transition-opacity before:duration-500 before:ease-out',
          displayBackground
            ? 'bg-[rgba(24,24,24,0.95)] shadow-[0_20px_45px_rgba(0,0,0,0.45)] before:opacity-100'
            : 'bg-transparent text-foreground/95',
          sticky && !menuIsOpened && 'shadow-[0_18px_40px_rgba(0,0,0,0.45)]',
          animationState === 'pending' && 'opacity-0 -translate-y-5',
          animationState === 'animate' && 'animate-header-slide-down'
        )}
      >
        <Container className="flex items-center justify-between gap-6">
          <div className="w-2/5 md:w-auto">
            <Link href="/" prefetch={false}>
              <Image
                src={logo}
                alt="PetrolBoys logo"
                priority
                className={twMerge(
                  'transition-all max-w-44',
                  displayBackground && 'drop-shadow-[0_10px_30px_rgba(0,0,0,0.35)]'
                )}
              />
            </Link>
          </div>

          <div className="lg:hidden">
            <HamburgerButton onClick={toggleMenu} isOpened={menuIsOpened} displayBackground={displayBackground} />
          </div>

          <div className="hidden lg:flex flex-row items-center">
            <nav>
              <MenuLinks />
            </nav>

            <Button as={Link} href="/kontakt" className="shadow-lg" variant="primary">
              Napisz do nas!
            </Button>
          </div>
        </Container>
      </header>
      {menuIsOpened && <MobileMenu isOpened={menuIsOpened} />}
    </>
  );
};
